import React, { Suspense, lazy, useEffect, useRef } from "react";

// Monaco is heavy (~3MB). React.lazy gives us code-splitting so the editor
// chunk only loads when a candidate actually hits a coding question.
const MonacoEditor = lazy(() => import("@monaco-editor/react"));

/**
 * Thin wrapper around @monaco-editor/react. Exposes:
 *   value          — controlled source code
 *   onChange       — fires on each keystroke (we use it to drive telemetry)
 *   onPaste        — fires when the user pastes into the editor
 *   language       — monaco language id (javascript/python/java/cpp/go/ruby/csharp)
 *   readOnly       — disable edits (used for solution/admin preview)
 *   height         — px or % string
 *
 * Hardening notes:
 *   - Right-click context menu disabled (one common bypass for paste)
 *   - F1 command palette disabled (less surface area for clipboard tricks)
 *   - Tab width 4 for Python, 2 elsewhere — matches plan-doc
 */
export default function CodeEditor({
  value,
  onChange,
  onPaste,
  language = "javascript",
  readOnly = false,
  height = "400px",
}) {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  const handleMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    if (onPaste) {
      editor.onDidPaste((e) => onPaste(e));
    }
  };

  useEffect(() => {
    const ed = editorRef.current;
    if (!ed) return;
    const tabSize = language === "python" ? 4 : 2;
    ed.updateOptions({ tabSize, insertSpaces: true });
  }, [language]);

  return (
    <Suspense fallback={<EditorSkeleton height={height} />}>
      <MonacoEditor
        height={height}
        language={language}
        value={value}
        theme="vs-dark"
        onChange={onChange}
        onMount={handleMount}
        options={{
          minimap: { enabled: false },
          contextmenu: false,       // disable right-click menu
          readOnly,
          fontSize: 13,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          tabSize: language === "python" ? 4 : 2,
          insertSpaces: true,
          renderLineHighlight: "line",
          padding: { top: 12, bottom: 12 },
        }}
      />
    </Suspense>
  );
}

function EditorSkeleton({ height }) {
  return (
    <div
      style={{ height }}
      className="bg-slate-900 rounded animate-pulse flex items-center justify-center text-slate-500 text-xs"
    >
      Loading editor…
    </div>
  );
}
